import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHotkey } from "@tanstack/react-hotkeys"
import { toast } from "react-toastify"
import {
  type Filter,
  filterByLabels,
  type FilterFieldType,
  type FilterOperatorType,
  getFilterOperators,
  normalizeFilterGroups,
} from "~/utils/filter"
import { useUserLookup } from "~/hooks/useUsers"
import { useAuth0 } from "@auth0/auth0-react"
import { trackEvent } from "~/hooks/useTrackingService"
import { formatFilterLabel } from "~/utils/filterLabel"
import { useFilters } from "~/hooks/useFilters"
import { useFilterFields } from "./useFilterFields"
import { useRef } from "react"
import { HOTKEYS } from "~/constants/hotkeys"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"
import { useRecentFilters } from "~/hooks/useRecentFilters"
import { useFilterSearchCommands } from "~/hooks/useFilterSearchCommands"

export type QuickFilterType = {
  name: string
  value: string
  type: string
  icon?: React.ReactNode
}

export type FilterMainItem =
  | { kind: "recent-field"; field: FilterFieldType }
  | { kind: "recent-quick"; quick: QuickFilterType; isNonBoolean: boolean }
  | { kind: "quick-row" }
  | { kind: "quick-match"; quick: QuickFilterType; isNonBoolean: boolean }
  | { kind: "field"; field: FilterFieldType }
  | { kind: "enum-match"; field: FilterFieldType; value: string }

type UseFilterPopoverArgs = {
  editingFilter?: Filter
  editingFilterIndex?: number
  shortcutEnabled?: boolean
  /** Adds "Add filter" / "Clear filters" to the command center; one instance per page */
  registerSearchCommands?: boolean
  saveInDB?: boolean
  /** Context-specific fields appended to the defaults (e.g. the supply plan's SKU Size). */
  extraFilterFields?: FilterFieldType[]
}

export function useFilterPopover({
  editingFilter,
  editingFilterIndex,
  shortcutEnabled = false,
  registerSearchCommands = false,
  saveInDB = true,
  extraFilterFields,
}: UseFilterPopoverArgs) {
  const { filters: savedFilters, setFilters: onFiltersChange, clearAll } = useFilters()
  const [localFilters, setLocalFilters] = useState<Filter[]>([])
  const [filterSearch, setFilterSearch] = useState("")
  const [open, setOpen] = useState(false)

  // Use local filters when saveInDB is false, otherwise use saved filters
  const filters = saveInDB ? savedFilters : localFilters
  const setFilters = useCallback(
    async (newFilters: Filter[]) => {
      if (saveInDB) {
        return onFiltersChange(newFilters)
      } else {
        setLocalFilters(newFilters)
        return Promise.resolve()
      }
    },
    [saveInDB, onFiltersChange],
  )

  const [selectedFilterField, setSelectedFilterField] = useState<FilterFieldType | null>(null)
  const [selectedFilterOperator, setSelectedFilterOperator] = useState<FilterOperatorType | null>(
    null,
  )
  const [selectedFilterLevel, setSelectedFilterLevel] = useState<string | null>("variantId")
  const [selectedFilterValue, setSelectedFilterValue] = useState<string>("")
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>([])
  const [selectedFilterGroupId, setSelectedFilterGroupId] = useState<number | undefined>(undefined)
  const lastEditingFilterRef = useRef<Filter | undefined>(undefined)

  const [showQuickFilters, setShowQuickFilters] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [focusedQuickFilterIndex, setFocusedQuickFilterIndex] = useState<number>(-1)

  const { user } = useAuth0()
  const { getUserName } = useUserLookup()
  const { recents, recordRecent } = useRecentFilters()

  const {
    enumLookupTable,
    allFilterFields,
    quickFilterFields,
    filterFields,
    nonBooleanQuickFilters,
    computeOptionsForField,
  } = useFilterFields(filterSearch, extraFilterFields)

  const resetFilterSelection = useCallback(() => {
    setSelectedFilterField(null)
    setSelectedFilterOperator(null)
    setSelectedFilterValue("")
    setSelectedFilterValues([])
    setSelectedFilterGroupId(undefined)
  }, [])

  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open && !editingFilter) {
        resetFilterSelection()
        setSelectedFilterLevel("variantId")
        setShowQuickFilters(false)
        setFilterSearch("")
        setFocusedIndex(-1)
        setFocusedQuickFilterIndex(-1)
      }
    },
    [editingFilter, resetFilterSelection],
  )

  const handleFilterFieldClick = useCallback(
    (filterField: FilterFieldType) => {
      setSelectedFilterField(filterField)
      setSelectedFilterOperator(
        getFilterOperators(
          filterField.value === "DynamicFilterField"
            ? (filterField.dynamicValue ?? "")
            : filterField.value,
          allFilterFields,
        )[0] ?? null,
      )
      setSelectedFilterValue("")
      setSelectedFilterValues([])
    },
    [allFilterFields],
  )

  const initializeFromEditingFilter = useCallback(() => {
    if (!editingFilter) return
    const field: FilterFieldType | undefined =
      allFilterFields.find(
        (f: FilterFieldType) =>
          f.value === editingFilter.field ||
          (f.value === "DynamicFilterField" && f.dynamicValue === editingFilter.field),
      ) ?? undefined
    if (field) {
      setSelectedFilterField(field)
      const operator: FilterOperatorType | undefined =
        getFilterOperators(editingFilter.field, allFilterFields).find(
          (op: FilterOperatorType) => op.value === editingFilter.operator,
        ) ?? undefined
      setSelectedFilterOperator(operator ?? null)
      setSelectedFilterLevel(editingFilter.by)
      setSelectedFilterValue(editingFilter.value)
      setSelectedFilterValues(editingFilter.values)
      setSelectedFilterGroupId(editingFilter.groupId)
    }
  }, [allFilterFields, editingFilter])

  useEffect(() => {
    if (editingFilter && editingFilter.field !== lastEditingFilterRef.current?.field) {
      lastEditingFilterRef.current = editingFilter
      initializeFromEditingFilter()
    } else if (!editingFilter && lastEditingFilterRef.current) {
      lastEditingFilterRef.current = undefined
      initializeFromEditingFilter()
    }
  }, [editingFilter, allFilterFields, initializeFromEditingFilter])

  const checkForDuplicateFilter = useCallback(
    (candidate: Filter, filtersToCheck: Filter[] = filters): boolean => {
      const existingFilter = filtersToCheck?.find(
        (f) =>
          f.field === candidate.field &&
          f.operator === candidate.operator &&
          f.value === candidate.value &&
          f.values.length === candidate.values.length &&
          f.values.every((val, i) => val === candidate.values[i]),
      )
      if (existingFilter) {
        toast.error("This exact filter already exists.")
        return true
      }
      return false
    },
    [filters],
  )

  const handleQuickFilterClick = useCallback(
    (
      filterType: { name: string; value: string; type: string; icon?: React.ReactNode },
      isNonBoolean: boolean = false,
    ) => {
      let value: string = ""
      let values: string[] = []
      let operator: string = "="

      if (isNonBoolean && filterType.name === "My SKUs") {
        value = user?.email ?? ""
        operator = "include"
      } else if (isNonBoolean) {
        value = ""
        values = ["NOOS"]
        operator = "oneOf"
      } else {
        value = "true"
      }

      const newFilter: Filter = {
        field: filterType.value,
        operator: operator,
        value: value,
        values: values,
        by: "variantId",
      }

      if (checkForDuplicateFilter(newFilter)) {
        return
      }

      trackEvent("Quick Filters Added", {
        Filters: [filterType.name],
      })

      const updatedFilters = filters ? [...filters, newFilter] : [newFilter]
      setFilters(updatedFilters).catch(() => {
        // Error handling is done by the mutation's onError handler
      })

      recordRecent({ kind: "quick", value: filterType.value, isNonBoolean })
      onOpenChange(false)
    },
    [filters, setFilters, onOpenChange, user?.email, checkForDuplicateFilter, recordRecent],
  )

  const handleQuickFiltersRowClick = useCallback(() => {
    setShowQuickFilters(true)
  }, [])

  const getTriggerText = useCallback(() => {
    if (!editingFilter) {
      return "Add filter"
    }

    const field = allFilterFields.find(
      (f) =>
        f.value === editingFilter.field ||
        (f.value === "DynamicFilterField" && f.dynamicValue === editingFilter.field),
    )
    const operator = getFilterOperators(editingFilter.field, allFilterFields).find(
      (op) => op.value === editingFilter.operator,
    )

    if (!field || !operator) {
      return "Edit filter"
    }

    return formatFilterLabel(editingFilter, allFilterFields, getUserName)
  }, [editingFilter, allFilterFields, getUserName])

  useEffect(() => {
    if (!selectedFilterField) return

    const fieldValue =
      selectedFilterField.value === "DynamicFilterField"
        ? (selectedFilterField.dynamicValue ?? "")
        : selectedFilterField.value

    const fieldOptions = computeOptionsForField(fieldValue, selectedFilterField.type)

    if (fieldOptions.length > 0) {
      setSelectedFilterValue(fieldOptions[0] ?? "")
    }
  }, [selectedFilterField, computeOptionsForField])

  useEffect(() => {
    if (showQuickFilters) {
      setShowQuickFilters(true)
    }
  }, [showQuickFilters])

  useEffect(() => {
    setFocusedIndex(-1)
    setFocusedQuickFilterIndex(-1)
  }, [filterSearch, showQuickFilters])

  const recentItems = useMemo<FilterMainItem[]>(() => {
    if (filterSearch !== "") return []
    return recents.flatMap<FilterMainItem>((r) => {
      if (r.kind === "field") {
        const field = filterFields.find(
          (f) =>
            f.value === r.value &&
            (r.value !== "DynamicFilterField" || f.dynamicValue === r.dynamicValue),
        )
        return field ? [{ kind: "recent-field", field }] : []
      }
      if (r.isNonBoolean) {
        const quick = nonBooleanQuickFilters.find((q) => q.value === r.value)
        return quick ? [{ kind: "recent-quick", quick, isNonBoolean: true }] : []
      }
      const quick = quickFilterFields.find((f) => f.value === r.value)
      return quick
        ? [
            {
              kind: "recent-quick",
              quick: { name: quick.name, value: quick.value, type: quick.type, icon: quick.icon },
              isNonBoolean: false,
            },
          ]
        : []
    })
  }, [recents, filterSearch, filterFields, nonBooleanQuickFilters, quickFilterFields])

  const handleEnumMatchClick = useCallback(
    (field: FilterFieldType, value: string) => {
      const fieldValue =
        field.value === "DynamicFilterField" ? (field.dynamicValue ?? "") : field.value

      const existing = filters?.find((f) => f.field === fieldValue)

      let updatedFilters: Filter[]
      if (existing) {
        // Merge into the existing filter — switch to oneOf and append the value if not already present
        const mergedValues = existing.values.includes(value)
          ? existing.values
          : [...existing.values, value]
        const mergedFilter: Filter = {
          ...existing,
          operator: "oneOf",
          value: "",
          values: mergedValues,
        }
        updatedFilters = filters.map((f) => (f.field === fieldValue ? mergedFilter : f))
        trackEvent("Filters Updated", {
          Filters: [field.name],
          Source: "Enum Quick Apply",
        })
      } else {
        const newFilter: Filter = {
          field: fieldValue,
          operator: "oneOf",
          value: "",
          values: [value],
          by: "variantId",
        }
        updatedFilters = filters ? [...filters, newFilter] : [newFilter]
        trackEvent("Filters Added", {
          Filters: [field.name],
          Source: "Enum Quick Apply",
        })
      }

      setFilters(updatedFilters).catch(() => {})
      recordRecent({ kind: "field", value: field.value, dynamicValue: field.dynamicValue })
      onOpenChange(false)
    },
    [filters, setFilters, onOpenChange, recordRecent],
  )

  const quickMatchItems = useMemo<FilterMainItem[]>(() => {
    if (filterSearch === "") return []
    const items: FilterMainItem[] = []
    for (const q of nonBooleanQuickFilters) {
      items.push({ kind: "quick-match", quick: q, isNonBoolean: true })
    }
    for (const q of quickFilterFields) {
      items.push({
        kind: "quick-match",
        quick: { name: q.name, value: q.value, type: q.type, icon: q.icon },
        isNonBoolean: false,
      })
    }
    return items
  }, [filterSearch, quickFilterFields, nonBooleanQuickFilters])

  const enumMatchItems = useMemo<FilterMainItem[]>(() => {
    if (filterSearch === "") return []
    const search = filterSearch.toLowerCase()
    const items: FilterMainItem[] = []
    for (const field of allFilterFields) {
      if (field.type !== "enum") continue
      const fieldValue =
        field.value === "DynamicFilterField" ? (field.dynamicValue ?? "") : field.value
      const options = computeOptionsForField(fieldValue, field.type)
      for (const option of options) {
        if (option.toLowerCase().includes(search)) {
          items.push({ kind: "enum-match", field, value: option })
        }
      }
    }
    return items
  }, [filterSearch, allFilterFields, computeOptionsForField])

  const filterFieldItems = useMemo<FilterMainItem[]>(() => {
    const recentKey = (f: FilterFieldType) =>
      `${f.value}::${f.value === "DynamicFilterField" ? (f.dynamicValue ?? "") : ""}`
    const recentKeys = new Set(
      recentItems
        .filter(
          (i): i is Extract<FilterMainItem, { kind: "recent-field" }> => i.kind === "recent-field",
        )
        .map((i) => recentKey(i.field)),
    )
    return filterFields
      .filter((f) => !recentKeys.has(recentKey(f)))
      .map((f) => ({ kind: "field" as const, field: f }))
  }, [filterFields, recentItems])

  const mainItems = useMemo<FilterMainItem[]>(() => {
    if (filterSearch !== "") {
      return [...filterFieldItems, ...quickMatchItems, ...enumMatchItems]
    }
    return [...recentItems, { kind: "quick-row" }, ...filterFieldItems]
  }, [filterSearch, filterFieldItems, quickMatchItems, enumMatchItems, recentItems])

  const handleMainItemActivate = useCallback(
    (item: FilterMainItem) => {
      switch (item.kind) {
        case "recent-field":
        case "field":
          handleFilterFieldClick(item.field)
          return
        case "recent-quick":
        case "quick-match":
          handleQuickFilterClick(item.quick, item.isNonBoolean)
          return
        case "quick-row":
          handleQuickFiltersRowClick()
          return
        case "enum-match":
          handleEnumMatchClick(item.field, item.value)
          return
      }
    },
    [
      handleFilterFieldClick,
      handleQuickFilterClick,
      handleQuickFiltersRowClick,
      handleEnumMatchClick,
    ],
  )

  const handleEscapeKeyDown = useCallback(
    (event: Event) => {
      if (selectedFilterField) {
        event.preventDefault()
        resetFilterSelection()
        return
      }
      if (showQuickFilters) {
        event.preventDefault()
        setShowQuickFilters(false)
        setFocusedQuickFilterIndex(-1)
        return
      }
    },
    [selectedFilterField, showQuickFilters, resetFilterSelection],
  )

  useHotkey(
    HOTKEYS.openFilterPopover.hotkey,
    () => {
      trackEvent("Filter Popover Opened", {
        Source: "Keyboard Shortcut",
        Shortcut: "Ctrl+Shift+F",
      })
      setOpen(true)
    },
    { enabled: shortcutEnabled, meta: shortcutMeta("Tables", "Add filter") },
  )

  useFilterSearchCommands({
    enabled: registerSearchCommands,
    hasFilters: savedFilters.length > 0,
    openFilters: () => {
      trackEvent("Filter Popover Opened", { Source: "Command Center" })
      setOpen(true)
    },
    clearFilters: () => void clearAll(),
  })

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((selectedFilterField || showQuickFilters) && event.key === "ArrowLeft") {
        event.preventDefault()
      }

      if (showQuickFilters) {
        const totalQuickFilters = nonBooleanQuickFilters.length + quickFilterFields.length

        switch (event.key) {
          case "ArrowDown":
            event.preventDefault()
            setFocusedQuickFilterIndex((prev) => (prev < totalQuickFilters - 1 ? prev + 1 : 0))
            break
          case "ArrowUp":
            event.preventDefault()
            setFocusedQuickFilterIndex((prev) => (prev > 0 ? prev - 1 : totalQuickFilters - 1))
            break
          case "Enter":
            event.preventDefault()
            if (focusedQuickFilterIndex >= 0 && focusedQuickFilterIndex < totalQuickFilters) {
              if (focusedQuickFilterIndex < nonBooleanQuickFilters.length) {
                const filterType = nonBooleanQuickFilters[focusedQuickFilterIndex]
                if (filterType) {
                  handleQuickFilterClick(filterType, true)
                }
              } else {
                const booleanIndex = focusedQuickFilterIndex - nonBooleanQuickFilters.length
                const filterType = quickFilterFields[booleanIndex]
                if (filterType) {
                  handleQuickFilterClick(filterType, false)
                }
              }
            }
            break
          case "ArrowLeft":
            event.preventDefault()
            setShowQuickFilters(false)
            setFocusedQuickFilterIndex(-1)
            break
        }
        return
      }

      if (selectedFilterField) {
        switch (event.key) {
          case "ArrowLeft":
            resetFilterSelection()
            break
        }
        return
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          setFocusedIndex((prev) => (prev < mainItems.length - 1 ? prev + 1 : 0))
          break
        case "ArrowUp":
          event.preventDefault()
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : mainItems.length - 1))
          break
        case "Enter":
        case "ArrowRight":
          event.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < mainItems.length) {
            const item = mainItems[focusedIndex]
            if (item) handleMainItemActivate(item)
          }
          break
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [
    open,
    selectedFilterField,
    focusedIndex,
    focusedQuickFilterIndex,
    mainItems,
    handleMainItemActivate,
    handleQuickFilterClick,
    showQuickFilters,
    nonBooleanQuickFilters,
    quickFilterFields,
  ])

  const filtersWithoutEditingFilter = useMemo(() => {
    if (editingFilterIndex != null) return filters.filter((_, i) => i !== editingFilterIndex)
    return filters.filter((f) => f.field !== editingFilter?.field)
  }, [filters, editingFilter, editingFilterIndex])

  const removeFilter = useCallback(() => {
    if (editingFilter) {
      setFilters(filtersWithoutEditingFilter).catch(() => {
        // Error handling is done by the mutation's onError handler
      })
    }
  }, [editingFilter, filtersWithoutEditingFilter, setFilters])

  const updateFilter = useCallback(
    (filterToUpdate: Filter, updatedFilter: Filter) => {
      const updatedFilters = filters.map((f, i) =>
        (
          editingFilterIndex != null
            ? i === editingFilterIndex
            : f.field === filterToUpdate.field && f.operator === filterToUpdate.operator
        )
          ? updatedFilter
          : f,
      )
      setFilters(normalizeFilterGroups(updatedFilters)).catch(() => {
        // Error handling is done by the mutation's onError handler
      })
      trackEvent("Filters Updated", {
        Filters: [selectedFilterField?.name ?? ""],
      })
    },
    [filters, setFilters, selectedFilterField?.name, editingFilterIndex],
  )

  const addFilter = useCallback(
    (newFilter: Filter) => {
      if (checkForDuplicateFilter(newFilter)) {
        return
      }

      const updatedFilters = filters ? [...filters, newFilter] : [newFilter]
      setFilters(normalizeFilterGroups(updatedFilters)).catch(() => {
        // Error handling is done by the mutation's onError handler
      })
      trackEvent("Filters Added", {
        Filters: [selectedFilterField?.name],
        Source: "Filter Popover",
      })
    },
    [filters, setFilters, checkForDuplicateFilter, selectedFilterField?.name],
  )

  const applyFilter = () => {
    if (!selectedFilterField || !selectedFilterOperator || !selectedFilterLevel) return

    const fieldValue =
      selectedFilterField.value === "DynamicFilterField"
        ? (selectedFilterField.dynamicValue ?? "")
        : selectedFilterField.value

    const hasValidValue = selectedFilterOperator.valueless
      ? true
      : selectedFilterOperator.arrayValue
        ? selectedFilterValues.length > 0
        : selectedFilterValue !== ""

    if (!hasValidValue) return

    const newFilter: Filter = {
      field: fieldValue,
      operator: selectedFilterOperator.value,
      value: selectedFilterOperator.valueless ? "" : selectedFilterValue,
      values: selectedFilterOperator.valueless ? [] : selectedFilterValues,
      by: selectedFilterLevel,
      gate: editingFilter?.gate,
      groupId: selectedFilterGroupId,
    }

    if (editingFilter) {
      updateFilter(editingFilter, newFilter)
    } else {
      addFilter(newFilter)
    }

    recordRecent({
      kind: "field",
      value: selectedFilterField.value,
      dynamicValue: selectedFilterField.dynamicValue,
    })
    onOpenChange(false)
  }

  const isFilterValid = useCallback((): boolean => {
    if (!selectedFilterField || !selectedFilterOperator || !selectedFilterLevel) return false

    if (selectedFilterOperator.valueless) return true

    const hasValidValue = selectedFilterOperator.arrayValue
      ? selectedFilterValues.length > 0
      : selectedFilterValue !== ""

    return hasValidValue
  }, [
    selectedFilterField,
    selectedFilterOperator,
    selectedFilterLevel,
    selectedFilterValue,
    selectedFilterValues,
  ])

  useEffect(() => {
    if (editingFilter) {
      const field = allFilterFields.find(
        (f) =>
          f.value === editingFilter.field ||
          (f.value === "DynamicFilterField" && f.dynamicValue === editingFilter.field),
      )
      if (field) {
        setSelectedFilterField(field)
        const operator = getFilterOperators(editingFilter.field, allFilterFields).find(
          (op) => op.value === editingFilter.operator,
        )
        setSelectedFilterOperator(operator ?? null)
        setSelectedFilterLevel(editingFilter.by)
        setSelectedFilterValue(editingFilter.value)
        setSelectedFilterValues(editingFilter.values)
      }
    }
  }, [editingFilter, allFilterFields])

  return {
    open,
    onOpenChange,
    filterSearch,
    setFilterSearch,
    selectedFilterField,
    selectedFilterOperator,
    selectedFilterLevel,
    selectedFilterValue,
    selectedFilterValues,
    setSelectedFilterOperator,
    setSelectedFilterLevel,
    setSelectedFilterValue,
    setSelectedFilterValues,
    showQuickFilters,
    setShowQuickFilters,
    focusedIndex,
    focusedQuickFilterIndex,
    allFilterFields,
    quickFilterFields,
    filterFields,
    nonBooleanQuickFilters,
    filtersWithoutEditingFilter,
    filterByLabels,
    resetFilterSelection,
    handleFilterFieldClick,
    handleQuickFilterClick,
    handleQuickFiltersRowClick,
    applyFilter,
    isFilterValid,
    getTriggerText,
    removeFilter,
    updateFilter,
    addFilter,
    enumLookupTable,
    checkForDuplicateFilter,
    filters,
    setFilters,
    localFilters,
    setLocalFilters,
    mainItems,
    handleMainItemActivate,
    handleEscapeKeyDown,
    hasRecents: recentItems.length > 0,
  }
}
